import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { Box, Grid, Stack, styled } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cell } from 'react-table';
import { ConfiguredEmailContextType, IEmail, saveEmail, testEmail } from 'wisestep-api';
import {
  WsColors,
  WsCard,
  CardVariant,
  SmtpSetupConfigIcon,
  WsButton,
  Item,
  WsDataTable,
  WsIconText,
  WsTypoColors,
  WsTypography,
} from 'wisestep-base';

import { SettingsHeader } from '../../../components/SettingsHeader';
import { useSKUModalStore } from '../../../components/skuModal/SKUModalStore';
import { WsBreadCrumbs } from '../../../components/WsBreadCrumbs';
import { useEmailConfigurationContext } from '../../../context';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../../../features/teams/helper/defaultValues';
import { setDefaultEmail, removeEmailAccount } from '../../../utils/emailHelper';
import { success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { emailConfigurationErrorHandler } from '../helper/errorHandler';
import WsSmtpSetup from '../WsSmtpSetup';

import CustomMailDailog from './CustomMailDialog';

const StyledStack = styled(Stack)({
  padding: '2',
  backgroundColor: WsColors.TheBlues50,
  gap: 3,
});

const CustomMail = () => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const setModal = useSKUModalStore((state) => state.setModal);
  const [showDialog, setshowDialog] = useState(false);
  const openDialog = () => setshowDialog(true);
  const closeDialog = () => setshowDialog(false);
  const navigate = useNavigate();
  const onTestHandler = async (emailConfig: IEmail) => {
    try {
      await testEmail({
        ...emailConfig,
      });
      setNotification(success('Test Email Sent Successfully'));
    } catch (error) {
      setNotification(emailConfigurationErrorHandler(error));
    }
  };
  const onSaveHandler = async (emailConfig: IEmail) => {
    try {
      await saveEmail({
        ...emailConfig,
      });
      setNotification(success('Account configured successfully'));
      navigate('/email-settings/custom-mail/');
    } catch (error: any) {
      if (error.response.status == 402) {
        setModal({ object: error.response.data, type: 'count' });
      }
      setNotification(emailConfigurationErrorHandler(error));
    }
  };

  const configuredContext = useEmailConfigurationContext();
  const [configuredEmails, setConfiguredEmail] = useState<ConfiguredEmailContextType[]>([]);
  const [hasData, setHasData] = useState<boolean>(false);
  useEffect(() => {
    if (configuredContext?.configuredData != null) {
      const customMailData = configuredContext.configuredData.filter(
        (data: ConfiguredEmailContextType) => {
          if (
            !(data.host === 'smtp.gmail.com') &&
            !(data.host === 'smtp.microsoft.com') &&
            !(data.host === 'smtp.mail.yahoo.com') &&
            !(data.host === 'smtpout.secureserver.net')
          )
            return data;
        }
      );
      if (customMailData.length > 0) {
        setHasData(true);
      }
      setConfiguredEmail(customMailData);
    }
  }, [configuredContext]);

  const customColumns = useMemo(
    () => [
      {
        Header: 'Account Name',
        accessor: 'account_name',
      },

      {
        Header: '',
        accessor: 'default_prop',
        Cell: (cell: Cell) => (
          <WsTypography color={WsTypoColors.TERTIARY}>{cell.value}</WsTypography>
        ),
      },
    ],
    []
  );
  const customData = useMemo(
    () =>
      configuredEmails?.map((accountsList) => ({
        account_name: accountsList.fromEmail,
        default_prop: accountsList.isDefault ? 'Default' : '',
      })),
    [configuredEmails]
  );
  return (
    <Box borderRadius={1.5}>
      <WsBreadCrumbs
        breadCrumbsList={getSettingBreadCrumbList(
          ISettingBreadCrumbTypes.APPS_AND_INTEGRATION,
          Pages['EMAIL_SETTINGS']
        )}
      />
      <SettingsHeader title="Email Settings" />
      <Grid container spacing={1}>
        <Grid item xs={2.5} marginTop={2} borderRadius={1.5}>
          <WsSmtpSetup />
        </Grid>
        <Grid marginTop={2} item xs={9.5}>
          {hasData === false ? (
            <Stack>
              <WsCard
                cardType={CardVariant.WITH_OUT_BG}
                title="No Custom Mail Account"
                description="Signin to one of your Custom Mail accounts to"
                middleComponent={<SmtpSetupConfigIcon />}
                isButton={true}
                buttonText="Add Custom Mail Account"
                btnStartIcon={<AddOutlinedIcon />}
                onClick={() => openDialog()}
              />
            </Stack>
          ) : (
            <>
              <StyledStack direction={'row'} justifyContent={'space-between'}>
                <Item>
                  <WsIconText icon={<SmtpSetupConfigIcon />} text="Custom Mail Accounts Added" />
                </Item>
                <Item>
                  <WsButton
                    onClick={() => openDialog()}
                    variant="contained"
                    size="medium"
                    startIcon={<AddOutlinedIcon />}
                  >
                    Add Custom Mail Account
                  </WsButton>
                </Item>
              </StyledStack>
              <Stack>
                <WsDataTable
                  columns={customColumns}
                  data={customData ?? []}
                  totalDataItems={customData?.length}
                  selectable={true}
                  preSelectedRows={[]}
                  boldItems={['account_name']}
                  rowActions={[
                    {
                      label: 'Default Account',
                      callback: async (a: Cell[]) =>
                        setDefaultEmail(
                          a,
                          configuredContext.configuredData,
                          configuredContext.setUpdateContext,
                          setNotification
                        ),
                    },
                    {
                      label: 'Remove',
                      callback: (cell: any, index: string | number) =>
                        removeEmailAccount(
                          customData[index].account_name,
                          configuredContext.configuredData,
                          configuredContext.setUpdateContext,
                          setNotification
                        ),
                    },
                  ]}
                />
              </Stack>
            </>
          )}
          <CustomMailDailog
            openDialog={showDialog}
            closeDialog={closeDialog}
            title={'Add Custom Email Account'}
            onSaveHandler={onSaveHandler}
            onTestHandler={onTestHandler}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
export default CustomMail;
